import React from 'react';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import OnboardThemeProvider from './components/OnboardThemeProvider';
import { notifySnackbar } from './components/snackbarBus';
import OnboardSnackbarProvider from './components/SnackbarProvider';
import Portal from './Portal/Portal';
import { PortalLogoContext } from './Portal/portalLogoContext';

import type { ComponentProps } from 'react';

import type { IPortalLogo } from './Portal/types';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const errorMessage = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : GENERIC_ERROR;

// Surface every failed query/mutation as an error snackbar, same as the admin
// SPA (see OnboardProviders.tsx). A query/mutation can opt out with
// `meta: { skipGlobalErrorSnackbar: true }`.
const skipGlobalSnackbar = (meta: Record<string, unknown> | undefined): boolean =>
  meta?.skipGlobalErrorSnackbar === true;

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (skipGlobalSnackbar(query.meta)) return;
      notifySnackbar(errorMessage(error), 'error');
    }
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (skipGlobalSnackbar(mutation.meta)) return;
      notifySnackbar(errorMessage(error), 'error');
    }
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

type Props = ComponentProps<typeof Portal> & { logo?: IPortalLogo | null };

const OnboardPortal = ({ logo = null, ...props }: Props) => (
  <OnboardThemeProvider>
    <OnboardSnackbarProvider>
      <QueryClientProvider client={queryClient}>
        <PortalLogoContext.Provider value={logo}>
          <Portal {...props} />
        </PortalLogoContext.Provider>
      </QueryClientProvider>
    </OnboardSnackbarProvider>
  </OnboardThemeProvider>
);

export default OnboardPortal;
